import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, limit, startAfter, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import './AdminLogViewer.css';

const AdminLogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState({
    level: 'ALL',
    category: 'ALL',
    eventType: 'ALL',
    startDate: '',
    endDate: ''
  });
  const [statistics, setStatistics] = useState(null);
  const [exporting, setExporting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    loadLogs();
    loadStatistics();
  }, []);

  const loadLogs = async (loadMore = false) => {
    try {
      setLoading(true);
      
      let q = query(
        collection(db, 'security_logs'),
        orderBy('timestamp', 'desc'),
        limit(50)
      );

      // Apply filters
      if (filters.level !== 'ALL') {
        q = query(q, where('level', '==', filters.level));
      }
      if (filters.category !== 'ALL') {
        q = query(q, where('category', '==', filters.category));
      }
      if (filters.eventType !== 'ALL') {
        q = query(q, where('eventType', '==', filters.eventType));
      }

      if (loadMore && lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const newLogs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().timestamp)
      }));

      if (loadMore) {
        setLogs(prev => [...prev, ...newLogs]);
      } else {
        setLogs(newLogs);
      }

      setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === 50);
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const getLogStatistics = httpsCallable(functions, 'getLogStatistics');
      const result = await getLogStatistics();
      setStatistics(result.data.statistics);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const applyFilters = () => {
    setLastDoc(null);
    setHasMore(true);
    loadLogs(false);
  };

  const exportLogs = async (format = 'json') => {
    try {
      setExporting(true);
      const exportLogsForSIEM = httpsCallable(functions, 'exportLogsForSIEM');
      
      const startDate = filters.startDate || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const endDate = filters.endDate || new Date().toISOString();
      
      const result = await exportLogsForSIEM({
        startDate,
        endDate,
        format
      });

      if (result.data.success) {
        // Download the exported data
        const dataStr = format === 'json' 
          ? JSON.stringify(result.data.logs, null, 2)
          : result.data.csv;
        
        const dataBlob = new Blob([dataStr], {
          type: format === 'json' ? 'application/json' : 'text/csv'
        });
        
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `security_logs_${new Date().toISOString().split('T')[0]}.${format}`;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting logs:', error);
    } finally {
      setExporting(false);
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      CRITICAL: '#ff4444',
      HIGH: '#ff8800',
      MEDIUM: '#ffbb00',
      LOW: '#88cc00',
      DEBUG: '#666666'
    };
    return colors[level] || '#666666';
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  if (loading && logs.length === 0) {
    return (
      <div className="admin-log-viewer">
        <div className="loading">Loading security logs...</div>
      </div>
    );
  }

  return (
    <div className="admin-log-viewer">
      <div className="log-header">
        <h1>Security Log Viewer</h1>
        <button 
          className="back-button"
          onClick={() => navigate('/admin')}
        >
          ← Back to Admin
        </button>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="log-statistics">
          <h3>Log Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Total Logs:</span>
              <span className="stat-value">{statistics.totalLogs.toLocaleString()}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Recent Activity (24h):</span>
              <span className="stat-value">{statistics.recentActivity.toLocaleString()}</span>
            </div>
            {Object.entries(statistics.byLevel).map(([level, count]) => (
              <div key={level} className="stat-item">
                <span className="stat-label" style={{ color: getLevelColor(level) }}>
                  {level}:
                </span>
                <span className="stat-value">{count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="log-filters">
        <h3>Filters</h3>
        <div className="filter-row">
          <select
            value={filters.level}
            onChange={(e) => handleFilterChange('level', e.target.value)}
          >
            <option value="ALL">All Levels</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
            <option value="DEBUG">Debug</option>
          </select>

          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
          >
            <option value="ALL">All Categories</option>
            <option value="AUTHENTICATION">Authentication</option>
            <option value="AUTHORIZATION">Authorization</option>
            <option value="ADMIN_ACTION">Admin Action</option>
            <option value="FINANCIAL">Financial</option>
            <option value="SECURITY">Security</option>
            <option value="USER_ACTION">User Action</option>
            <option value="SYSTEM">System</option>
          </select>

          <input
            type="datetime-local"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            placeholder="Start Date"
          />

          <input
            type="datetime-local"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            placeholder="End Date"
          />

          <button onClick={applyFilters} className="apply-filters-btn">
            Apply Filters
          </button>
        </div>

        <div className="export-buttons">
          <button 
            onClick={() => exportLogs('json')} 
            disabled={exporting}
            className="export-btn"
          >
            {exporting ? 'Exporting...' : 'Export JSON'}
          </button>
          <button 
            onClick={() => exportLogs('csv')} 
            disabled={exporting}
            className="export-btn"
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="logs-table-container">
        <table className="logs-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Level</th>
              <th>Category</th>
              <th>Event Type</th>
              <th>User ID</th>
              <th>Message</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id} className={`log-row level-${log.level.toLowerCase()}`}>
                <td>{formatTimestamp(log.timestamp)}</td>
                <td>
                  <span 
                    className="level-badge"
                    style={{ backgroundColor: getLevelColor(log.level) }}
                  >
                    {log.level}
                  </span>
                </td>
                <td>{log.category}</td>
                <td>{log.eventType}</td>
                <td>{log.userId || 'N/A'}</td>
                <td className="message-cell">{log.message}</td>
                <td>
                  <details>
                    <summary>View Details</summary>
                    <pre>{JSON.stringify(log.details, null, 2)}</pre>
                  </details>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="load-more-container">
          <button 
            onClick={() => loadLogs(true)}
            disabled={loading}
            className="load-more-btn"
          >
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}

      {logs.length === 0 && !loading && (
        <div className="no-logs">
          No logs found matching the current filters.
        </div>
      )}
    </div>
  );
};

export default AdminLogViewer;
